import Immutable from 'immutable';
import {
	getGlobalContextPath,
	REPORTED_EXCEPTION,
	setGlobalContext,
	store,
	webapi
} from 'ps-gui';
import React from 'react';
import { initializeLookups } from './lookup';
import { setPhraseLanguage } from './phrases';
import { initializePreferences } from './preferences';
import {
	checkLogoutImminentIn,
	setLoggedOut,
	setLogoutIn
} from './session';

const globalContextPath = getGlobalContextPath();

const EMPTY_MAP = Immutable.Map({});

/**
 * Class GlobalContextModel reprezentuje globalni context aplikace. obsahuje informace o sessione, vybranem jazyce, atd.
 */
class GlobalContextModel {
	// ------------------------------------------------------ shared
	#data;

	constructor(data) {
		this.#data = data || store.getState().getIn(globalContextPath) || EMPTY_MAP;
	}

	#setProperty(name, value) {
		if (Array.isArray(name)) {
			return new GlobalContextModel(this.#data.setIn(name, value));
		} else {
			return new GlobalContextModel(this.#data.set(name, value));
		}
	}

	fromAppState(state) {
		const data = state.getIn(globalContextPath);
		if (data === this.#data) {
			return this;
		} else {
			return new GlobalContextModel(data);
		}
	}

	apply() {
		setGlobalContext(this.#data);
		return this;
	}

	// ------------------------------------------------------- application specific

	#setLoginTypeId(loginTypeId) {
		const isAdmin = loginTypeId === 'HADMIN' || loginTypeId === 'OTPADMIN';
		const isSubmissionAdmin = loginTypeId === 'HSUBADMIN' || loginTypeId === 'OTPSUBADMIN';
		return this.#setProperty('loginTypeId', loginTypeId).#setProperty('isAdmin', isAdmin).#setProperty('isSubmissionAdmin', isSubmissionAdmin);
	}

	getLanguageId() {
		return this.#data.get('languageId') || 'CZ';
	}

	async setLanguageId(langId) {
		localStorage.setItem('languageId', langId);
		setPhraseLanguage(langId);
		if (this.isLoggedIn()) {
			await webapi.execute('/api/proc/api_user/setLanguageId', { languageId: langId });
			await initializeLookups();
		}
		return this.#setProperty('languageId', langId).#setProperty('languageTimeStamp', new Date().getTime());
	}

	getLanguageTimestamp() {
		return this.#data.get('languageTimeStamp') || 0;
	}

	isAdmin() {
		return this.#data.get('isAdmin');
	}

	isSubmissionAdmin() {
		return this.#data.get('isSubmissionAdmin');
	}

	hasRecordings() {
		return this.#data.get('hasRecordings');
	}

	getMaintenanceDay() {
		return this.#data.get('maintenanceDay');
	}

	getMaintenanceTimeFrom() {
		return this.#data.get('maintenanceTimeFrom');
	}

	getMaintenanceTimeTo() {
		return this.#data.get('maintenanceTimeTo');
	}

	getMaintenance() {
		return this.#data.get('maintenance');
	}

	closeMaintenanceNotification() {
		if (!this.getMaintenanceDay()) {
			return this;
		}
		webapi.execute('/api/proc/api_systemsettings/closeSystemMaintenanceNotification', { id: this.#data.get('maintenanceId') }, null, true);
		return this.#setProperty('maintenanceDay', null).#setProperty('maintenanceTimeFrom', null).#setProperty('maintenanceTimeTo', null);
	}

	getNotifications() {
		return this.#data.get('notifications');
	}

	closeNotification(id) {
		let notifications = this.#data.get('notifications');
		if (!notifications) {
			return this;
		}
		webapi.execute('/api/proc/api_notification/closeNotification', { id }, null, true);
		return this.#setProperty('notifications', notifications.filter(n => n.get('id') !== id));
	}

	async setSessionToken(token, options) {
		let model = this.#setProperty('sessionToken', token);
		webapi.setDefaultHeaders({
			token
		});
		await initializePreferences();
		model = await model.loadUser(options);
		return model;
	}

	async loadUser(options) {
		options = options || {};
		let ui = await webapi.execute('/api/proc/api_user/getuserinfo');
		if (ui === REPORTED_EXCEPTION) {
			return this;
		}
		let languageId = ui.languageId || 'CZ';
		if (options.overrideUserLanguage && languageId !== this.getLanguageId()) {
			languageId = this.getLanguageId();
			await webapi.execute('/api/proc/api_user/setLanguageId', {
				languageId
			});
		}
		setPhraseLanguage(languageId);
		// setPhraseLanguage('EN');
		let model = this.#setProperty('userDisplayName', ui.userDisplayName)
			.#setProperty('userId', ui.userId)
			.#setProperty('hasBeenLoggedIn', true)
			.#setProperty('hasRecordings', ui.hasRecordings)
			.#setProperty('maintenanceId', ui.maintenanceId)
			.#setProperty('maintenanceDay', ui.maintenanceDay)
			.#setProperty('maintenanceTimeFrom', ui.maintenanceTimeFrom)
			.#setProperty('maintenanceTimeTo', ui.maintenanceTimeTo)
			.#setProperty('languageId', languageId)
			.#setProperty('notifications', Immutable.fromJS(ui.notifications))
			.#setLoginTypeId(ui.loginTypeId);

		if (ui.fixedPartyId || ui.selectedPartyId) {
			model = await model.setPartyId(ui.fixedPartyId || ui.selectedPartyId);
		}
		return model;
	}

	hasBeenLoggedIn() {
		return this.#data.get('hasBeenLoggedIn');
	}

	async setPartyId(partyId) {
		return this.#setProperty('partyId', partyId);
	}

	getPartyId() {
		return this.#data.get('partyId');
	}

	isLoggedIn() {
		return !!this.#data.get('userId');
	}

	getUserId() {
		return this.#data.get('userId');
	}

	getUserDisplayName() {
		return this.#data.get('userDisplayName');
	}

	setLoggedOut() {
		return this.#setProperty('loggedOut', true).#setProperty('userId', null).#clearLogoutInTimeout();
	}

	clearLogoutImminent() {
		if (this.#data.get('maintenance')) {
			return this;
		}
		return this.#setProperty('logoutIn', -1).#clearLogoutInTimeout();
	}

	#clearLogoutInTimeout() {
		let timeoutId = this.#data.get('lougoutInTimeout');
		if (timeoutId) {
			window.clearTimeout(timeoutId);
		} else {
			return this;
		}
		return this.#setProperty('lougoutInTimeout', null);
	}

	getLogoutIn() {
		return this.#data.get('logoutIn');
	}

	setLogoutIn(seconds) {
		if (seconds < 2) {
			window.setTimeout(() => {
				setLoggedOut();
			}, 2);
			throw 'logout';
		}
		if (this.isLoggedOut()) {
			return this;
		}
		// console.log('logoutIn', seconds);
		let ctx = this.#clearLogoutInTimeout();
		let nextSecs = seconds % 60;
		if (nextSecs === 0) {
			nextSecs = 60;
		}
		// console.log('nextSecs = ', nextSecs, 'willBe = ', seconds - nextSecs);
		let timeoutId = setTimeout(() => {
			setLogoutIn(seconds - nextSecs);
		}, nextSecs * 1000);
		return ctx.#setProperty('logoutIn', seconds).#setProperty('lougoutInTimeout', timeoutId);
	}

	async checkLogoutImminent() {
		if (this.isLoggedOut()) {
			return this;
		}
		let res = await webapi.execute('/api/proc/api_user/lanu_getSessionState', null, null, true);
		console.log(res);

		if (res.sessionSecondsRemaining < 0) {
			window.setTimeout(() => {
				setLoggedOut();
			}, 2);
			throw 'logout';
		}

		if (res === REPORTED_EXCEPTION) {
			return this;
		}
		let warningIn = res.sessionSecondsRemaining - res.logoutWarningThreshold;
		console.log('win = ', warningIn, res.sessionSecondsRemaining, res.logoutWarningThreshold);
		let ctx = this;
		if (warningIn <= 0) {
			ctx = ctx.setLogoutIn(res.sessionSecondsRemaining);
		} else {
			checkLogoutImminentIn(warningIn + 2);
		}
		return ctx.#setProperty('maintenance', res.maintenance);
	}

	isLoggedOut() {
		return this.#data.get('loggedOut');
	}
}

export const GlobalContext = React.createContext();

export function withGlobalContext(Component) {
	return props => {
		return (
			<GlobalContext.Consumer>
				{
					globalContext =>
						<Component
							globalContext={globalContext}
							{...props}
						/>

				}
			</GlobalContext.Consumer>
		);
	};
}

export { GlobalContextModel };
